import React, { useEffect, useState, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useToast } from '../../../../hooks';
import proofkitFilemakerService from '../../../../services/proofkitFilemakerService';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Button,
  Input,
  Label,
  Checkbox,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '../../../../components/ui';
import { ArrowLeft, Save, Send } from "lucide-react";
import { ZCampaigns } from '../../../../schema/Campaigns';
import formatters from '../../../../styles/tokens/formatters';

const CAMPAIGNS_LAYOUT = process.env.REACT_APP_FILEMAKER_LAYOUT_CAMPAIGNS || "campaigns";
const TEMPLATES_LAYOUT = process.env.REACT_APP_FILEMAKER_LAYOUT_TEMPLATES || "templates";
const CONTACTS_LAYOUT = process.env.REACT_APP_FILEMAKER_LAYOUT_CONTACTS || "contacts";
const CAMPAIGN_RECIPIENTS_LAYOUT = process.env.REACT_APP_FILEMAKER_LAYOUT_CAMPAIGN_RECIPIENTS || "campaign_recipients";
const SEGMENTS_LAYOUT = process.env.REACT_APP_FILEMAKER_LAYOUT_SEGMENTS || "segments";
const CONTACT_SEGMENTS_LAYOUT = process.env.REACT_APP_FILEMAKER_LAYOUT_CONTACT_SEGMENTS || "contact_segments";

export default function CampaignsFormEdit() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [campaign, setCampaign] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [segmentMap, setSegmentMap] = useState({});
  const [existingRecipients, setExistingRecipients] = useState([]);

  const [formData, setFormData] = useState({
    nombre: "",
    template_id: "",
    estado: "borrador",
    fecha_envio: "",
  });

  const [contactsQuery, setContactsQuery] = useState("");
  const [selectedContactIds, setSelectedContactIds] = useState(() => new Set());

  const templatesOptions = useMemo(() => {
    return (Array.isArray(templates) ? templates : []).map((r) => {
      const fieldData = r?.fieldData || {};
      const id = fieldData?.ClavePrincipal || r?.recordId || "";
      const nombre = fieldData?.nombre || id;
      return { id, nombre };
    });
  }, [templates]);

  const contactsRows = useMemo(() => {
    const q = String(contactsQuery || "").trim().toLowerCase();
    const base = (Array.isArray(contacts) ? contacts : []).map((r) => {
      const fieldData = r?.fieldData || {};
      const id = fieldData?.ClavePrincipal || r?.recordId || "";
      const nombre = fieldData?.nombre || "";
      const apellido = fieldData?.apellido || "";
      const email = fieldData?.email || "";
      const fullName = `${nombre} ${apellido}`.trim();
      const segments = segmentMap[id] || [];
      return { id, nombre, apellido, email, fullName, segments };
    });

    if (!q) return base;

    return base.filter((c) => {
      const h = `${c.fullName} ${c.email}`.toLowerCase();
      return h.includes(q);
    });
  }, [contacts, contactsQuery, segmentMap]);

  const selectedCount = useMemo(() => selectedContactIds.size, [selectedContactIds]);

  const toggleContact = (id, checked) => {
    setSelectedContactIds((prev) => {
      const next = new Set(prev);
      if (checked) next.add(id);
      else next.delete(id);
      return next;
    });
  };

  const toggleAllFiltered = (checked) => {
    setSelectedContactIds((prev) => {
      const next = new Set(prev);
      contactsRows.forEach((c) => {
        if (checked) next.add(c.id);
        else next.delete(c.id);
      });
      return next;
    });
  };

  const allFilteredSelected = useMemo(() => {
    if (!contactsRows.length) return false;
    return contactsRows.every((c) => selectedContactIds.has(c.id));
  }, [contactsRows, selectedContactIds]);

  const someFilteredSelected = useMemo(() => {
    if (!contactsRows.length) return false;
    return contactsRows.some((c) => selectedContactIds.has(c.id));
  }, [contactsRows, selectedContactIds]);

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        const [campaignRes, templatesRes, contactsRes, segmentsRes, contactSegmentsRes] = await Promise.all([
          proofkitFilemakerService.getRecord(CAMPAIGNS_LAYOUT, id),
          proofkitFilemakerService.getAllRecords(TEMPLATES_LAYOUT),
          proofkitFilemakerService.getAllRecords(CONTACTS_LAYOUT),
          proofkitFilemakerService.getAllRecords(SEGMENTS_LAYOUT),
          proofkitFilemakerService.getAllRecords(CONTACT_SEGMENTS_LAYOUT),
        ]);

        const campaignData = campaignRes?.data;
        if (campaignData?.fieldData) {
          const fd = campaignData.fieldData;
          setCampaign(campaignData);
          setFormData({
            nombre: fd.nombre || "",
            template_id: fd.template_id || "",
            estado: fd.estado || "borrador",
            fecha_envio: formatters.toLocalDateTimeInputFromFileMaker(fd.fecha_envio || ""),
          });
        }

        setTemplates(Array.isArray(templatesRes?.data) ? templatesRes.data : []);
        setContacts(Array.isArray(contactsRes?.data) ? contactsRes.data : []);
        
        const campaignKey = campaignData?.fieldData?.ClavePrincipal || campaignData?.recordId || id;
        const recipientsRes = await proofkitFilemakerService.findRecords(CAMPAIGN_RECIPIENTS_LAYOUT, {
          query: [{ campaign_id: campaignKey }],
          limit: 100,
        });
        setExistingRecipients(Array.isArray(recipientsRes?.data) ? recipientsRes.data : []);

        const segmentsRaw = Array.isArray(segmentsRes?.data) ? segmentsRes.data : [];
        const contactSegmentsRaw = Array.isArray(contactSegmentsRes?.data) ? contactSegmentsRes.data : [];

        const segmentsById = {};
        segmentsRaw.forEach((row) => {
          const fd = row?.fieldData || {};
          const segId = fd?.ClavePrincipal || row?.recordId;
          const nombre = fd?.nombre || "";
          if (segId) segmentsById[String(segId)] = nombre;
        });

        const map = {};
        contactSegmentsRaw.forEach((row) => {
          const fd = row?.fieldData || {};
          const contactId = fd?.contact_id;
          const segmentId = fd?.segment_id;
          if (!contactId || !segmentId) return;
          const segName = segmentsById[String(segmentId)] || "";
          if (!segName) return;
          const key = String(contactId);
          map[key] = map[key] || [];
          if (!map[key].includes(segName)) {
            map[key].push(segName);
          }
        });

        setSegmentMap(map);

        // Set existing contact IDs as selected
        const existingContactIds = (Array.isArray(recipientsRes?.data) ? recipientsRes.data : [])
          .map(r => r?.fieldData?.contact_id)
          .filter(Boolean);
        setSelectedContactIds(new Set(existingContactIds));

      } catch (err) {
        toast({
          title: "Error",
          description: "No se pudieron cargar los datos desde FileMaker",
          variant: "destructive",
        });
        setTemplates([]);
        setContacts([]);
        setSegmentMap({});
        setExistingRecipients([]);
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [id, toast]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((p) => ({ ...p, [name]: value }));
  };

  const handleSelectChange = (name, value) => {
    setFormData((p) => ({ ...p, [name]: value }));
  };

  const onSubmit = async (e) => {
    e.preventDefault();

    const nombre = String(formData?.nombre || "").trim();
    const templateId = String(formData?.template_id || "").trim();

    if (!nombre) {
      return toast({ title: "Falta nombre", description: "Debes indicar el nombre de la campaña", variant: "destructive" });
    }

    if (!templateId) {
      return toast({ title: "Falta plantilla", description: "Debes seleccionar una plantilla", variant: "destructive" });
    }

    if (!selectedContactIds.size) {
      return toast({ title: "Faltan contactos", description: "Debes seleccionar al menos 1 contacto", variant: "destructive" });
    }

    try {
      setSaving(true);

      // Update campaign
      const campaignFieldData = ZCampaigns
        .pick({ nombre: true, template_id: true, estado: true, fecha_envio: true })
        .partial()
        .parse({
          nombre,
          template_id: templateId,
          estado: formData?.estado || "borrador",
        });

      const fechaEnvioRaw = String(formData?.fecha_envio || "").trim();
      if (fechaEnvioRaw) {
        const fechaEnvio = formatters.toFileMakerDateTimeUS24(fechaEnvioRaw);
        if (fechaEnvio) campaignFieldData.fecha_envio = fechaEnvio;
      }

      await proofkitFilemakerService.updateRecord(CAMPAIGNS_LAYOUT, id, campaignFieldData);

      // Handle recipients
      const existingContactIds = new Set(existingRecipients.map(r => r?.fieldData?.contact_id).filter(Boolean));
      const newContactIds = Array.from(selectedContactIds).filter(contactId => !existingContactIds.has(contactId));
      const removedContactIds = Array.from(existingContactIds).filter(contactId => !selectedContactIds.has(contactId));

      // Remove old recipients
      if (removedContactIds.length > 0) {
        const removePromises = existingRecipients
          .filter(r => removedContactIds.includes(r?.fieldData?.contact_id))
          .map(r => proofkitFilemakerService.deleteRecord(CAMPAIGN_RECIPIENTS_LAYOUT, r?.fieldData?.ClavePrincipal || r?.recordId));
        
        await Promise.all(removePromises);
      }

      // Add new recipients
      if (newContactIds.length > 0) {
        const campaignKey = campaign?.fieldData?.ClavePrincipal || campaign?.recordId || id;
        const contactKeys = await Promise.all(
          newContactIds.map(async (cid) => {
            try {
              const res = await proofkitFilemakerService.getRecord(CONTACTS_LAYOUT, cid);
              const fd = res?.data?.fieldData || {};
              return fd?.ClavePrincipal || cid;
            } catch (_) {
              const fr = await proofkitFilemakerService.findRecords(CONTACTS_LAYOUT, { query: [{ ClavePrincipal: cid }], limit: 1 });
              const fd = fr?.data?.[0]?.fieldData || {};
              return fd?.ClavePrincipal || cid;
            }
          })
        );
        const createPromises = contactKeys.map((contactKey) =>
          proofkitFilemakerService.createRecord(CAMPAIGN_RECIPIENTS_LAYOUT, {
            campaign_id: campaignKey,
            contact_id: contactKey,
            estado: "pendiente",
          })
        );
        await Promise.all(createPromises);
      }

      toast({ title: "Campaña actualizada", description: "La campaña fue actualizada con éxito" });
      navigate("/campanas");
    } catch (err) {
      console.error("Error updating campaign:", err);
      toast({ title: "Error", description: "No se pudo actualizar la campaña", variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <Button variant="ghost" onClick={() => navigate("/campanas")}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Volver
          </Button>
        </div>
        <div className="text-center py-8">Cargando campaña...</div>
      </div>
    );
  }

  if (!campaign) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <Button variant="ghost" onClick={() => navigate("/campanas")}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Volver
          </Button>
        </div>
        <div className="text-center py-8 text-[var(--yool-error)]">Campaña no encontrada</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <Button variant="ghost" onClick={() => navigate("/campanas")}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Volver
        </Button>
        <Button onClick={onSubmit} disabled={saving}>
          <Save className="h-4 w-4 mr-2" />
          {saving ? "Guardando..." : "Guardar Cambios"}
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Editar Campaña</CardTitle>
        </CardHeader>
        <CardContent>
          <form className="space-y-6" onSubmit={onSubmit}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="nombre">Nombre</Label>
                <Input 
                  id="nombre" 
                  name="nombre" 
                  value={formData.nombre} 
                  onChange={handleInputChange} 
                />
              </div>

              <div className="space-y-2">
                <Label>Plantilla</Label>
                <Select value={formData.template_id} onValueChange={(v) => handleSelectChange("template_id", v)}>
                  <SelectTrigger>
                    <SelectValue placeholder="Selecciona una plantilla" />
                  </SelectTrigger>
                  <SelectContent>
                    {templatesOptions.map((t) => (
                      <SelectItem key={t.id} value={t.id}>
                        {t.nombre}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Estado</Label>
                <Select value={formData.estado} onValueChange={(v) => handleSelectChange("estado", v)}>
                  <SelectTrigger>
                    <SelectValue placeholder="Selecciona estado" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="borrador">Borrador</SelectItem>
                    <SelectItem value="programada">Programada</SelectItem>
                    <SelectItem value="enviada">Enviada</SelectItem>
                    <SelectItem value="cancelada">Cancelada</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="fecha_envio">Fecha envío</Label>
                <Input
                  id="fecha_envio"
                  name="fecha_envio"
                  type="datetime-local"
                  value={formData.fecha_envio}
                  onChange={handleInputChange}
                />
              </div>
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>Contactos ({selectedCount} seleccionados)</Label>
                <div className="w-full max-w-sm">
                  <Input
                    placeholder="Buscar por nombre o email..."
                    value={contactsQuery}
                    onChange={(e) => setContactsQuery(e.target.value)}
                  />
                </div>
              </div>

              <div className="rounded-md border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-12">
                        <div className="flex items-center justify-center">
                          <Checkbox
                            checked={allFilteredSelected || (someFilteredSelected && "indeterminate")}
                            onCheckedChange={(v) => toggleAllFiltered(!!v)}
                            aria-label="Seleccionar todos"
                          />
                        </div>
                      </TableHead>
                      <TableHead>Nombre</TableHead>
                      <TableHead>Email</TableHead>
                      <TableHead>Segmento(s)</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {contactsRows.length ? (
                      contactsRows.map((c) => (
                        <TableRow key={c.id} data-state={selectedContactIds.has(c.id) ? "selected" : undefined}>
                          <TableCell>
                            <div className="flex items-center justify-center">
                              <Checkbox
                                checked={selectedContactIds.has(c.id)}
                                onCheckedChange={(v) => toggleContact(c.id, !!v)}
                                aria-label="Seleccionar contacto"
                              />
                            </div>
                          </TableCell>
                          <TableCell className="font-medium">{c.fullName || "Sin nombre"}</TableCell>
                          <TableCell>{c.email || "-"}</TableCell>
                          <TableCell>{(c.segments || []).join(", ") || "-"}</TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={4} className="h-24 text-center">
                          No hay contactos.
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
